import { useRef, type FC, type HTMLAttributeAnchorTarget } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { LazyImage } from './LazyImage.tsx';
import { useTileSizeClassNames } from '../hooks/useTileSizeClassNames.ts';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';

import {
    getBackgroundColor,
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from '../constants.ts';
import { withBuyerHost } from '../helpers/withBuyerHost.ts';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

import { type ProductImage_item$key } from './__generated__/ProductImage_item.graphql.ts';
import { type ProductImage_photo$key } from './__generated__/ProductImage_photo.graphql.ts';

const itemFragment = graphql`
    fragment ProductImage_item on Item {
        title
        localizedPdpUrl
    }
`;

const photoFragment = graphql`
    fragment ProductImage_photo on ItemPhoto {
        masterOrZoomPath
        smallPath
        versions {
            webPath
        }
    }
`;

type Props = {
    item: ProductImage_item$key | null | undefined;
    photo: ProductImage_photo$key | null | undefined;
    buyerHost?: string;
    dataTn?: string;
    disableLinks?: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    imageDraggable?: boolean;
    imageLoadVerticalOffset?: number;
    imagePath?: string | null;
    isEagerImageLoadEnabled: boolean;
    hasVideo: boolean | null | undefined;
    linkTarget?: HTMLAttributeAnchorTarget;
    onContentLoaded?: () => void;
    onClick?: OnClick;
    size: TileSize;
    srcSet?: SrcSet;
};

export const ProductImage: FC<Props> = ({
    item: itemRef,
    photo: photoRef,
    buyerHost,
    dataTn,
    disableLinks,
    imageBackgroundColor,
    imageDraggable,
    imageLoadVerticalOffset,
    imagePath,
    isEagerImageLoadEnabled,
    hasVideo,
    linkTarget,
    onClick,
    onContentLoaded,
    size,
    srcSet,
}) => {
    const hasLazyLoaded = useRef(false);
    const item = useFragment(itemFragment, itemRef);
    const photo = useFragment(photoFragment, photoRef);
    const pdpUrl = withBuyerHost(item?.localizedPdpUrl, buyerHost) || '';
    const title = item?.title || '';
    const smallPath = photo?.smallPath;
    const webPath = photo?.versions?.[0]?.webPath || '';
    const src = imagePath || smallPath || webPath;
    const pathForSrcSet = imagePath || photo?.masterOrZoomPath;

    const backgroundColor = getBackgroundColor(imageBackgroundColor);
    const { productImageClassNames } = useTileSizeClassNames({ size });
    const classNameList = classnames(
        dibsCss.objectContain,
        dibsCss.wFull,
        dibsCss.hFull,
        dibsCss.pXsmall,
        {
            [styles.imageVideo]: hasVideo,
            [styles.imageBackground]: backgroundColor,
        }
    );

    const srcSetAttributes =
        srcSet?.sizes && pathForSrcSet
            ? {
                  srcSet: getSrcsetString(pathForSrcSet, srcSet?.widths, {
                      quality: srcSet?.quality,
                  }),
                  sizes: srcSet.sizes,
              }
            : {};

    const image = (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.justifyCenter,
                dibsCss.itemsCenter,
                dibsCss.wAuto,
                dibsCss.m0,
                backgroundColor,
                productImageClassNames
            )}
            data-tn="product-image-container"
        >
            {isEagerImageLoadEnabled && !hasLazyLoaded.current ? (
                <img
                    className={classnames(classNameList)}
                    {...srcSetAttributes}
                    src={src}
                    alt={title}
                    data-tn="product-image"
                    draggable={imageDraggable}
                    fetchPriority="high"
                    onLoad={() => onContentLoaded?.()}
                />
            ) : (
                <>
                    <LazyImage
                        className={classNameList}
                        srcSetAttributes={srcSetAttributes}
                        alt={title}
                        offsetVertical={imageLoadVerticalOffset}
                        onImageLoad={() => {
                            hasLazyLoaded.current = true;
                            onContentLoaded?.();
                        }}
                        src={src}
                        imageDraggable={imageDraggable}
                    />
                    <noscript>
                        <img
                            className={classNameList}
                            src={src}
                            alt={title}
                            {...srcSetAttributes}
                        />
                    </noscript>
                </>
            )}
        </div>
    );

    return disableLinks ? (
        image
    ) : (
        <SeoLink
            dataTn={dataTn || 'product-image-link'}
            linkData={{ path: pdpUrl, isLinkable: false }}
            onClick={onClick}
            className={dibsCss.wFull}
            target={linkTarget}
        >
            {image}
        </SeoLink>
    );
};
